import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { MtTooltip } from '@components/MtTooltip';
import { useRaceTrackStore } from '../useRaceTrackStore';

interface TrackToolbarProps {
  onCapture: () => void;
  onUpdateSelected: () => void;
  onDeleteSelected: () => void;
  onDeleteAll: () => void;
  onPlayToggle: () => void;
  onJumpToStart: () => void;
  onJumpToPrevious: () => void;
  onJumpToNext: () => void;
  onJumpToEnd: () => void;
  onOptimize: () => void;
  onExport: () => void;
  onTogglePreview: () => void;
  onClose: () => void;
}

function ToolButton({
  title,
  children,
  disabled,
  onPress,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
  onPress?: () => void;
  className?: string;
}) {
  return (
    <MtTooltip content={title} placement="bottom">
      <Button
        isIconOnly
        size="sm"
        variant="secondary"
        aria-label={title}
        isDisabled={disabled}
        onPress={onPress}
        className={`h-8 w-8 min-w-8 rounded-md bg-transparent p-0 text-white/75 hover:bg-white/10 hover:text-white disabled:opacity-40 [&_svg]:size-4 ${className}`}
      >
        {children}
      </Button>
    </MtTooltip>
  );
}

export function TrackToolbar({
  onCapture,
  onUpdateSelected,
  onDeleteSelected,
  onDeleteAll,
  onPlayToggle,
  onJumpToStart,
  onJumpToPrevious,
  onJumpToNext,
  onJumpToEnd,
  onOptimize,
  onExport,
  onTogglePreview,
  onClose,
}: TrackToolbarProps) {
  const { t } = useTranslation();
  const keyframes = useRaceTrackStore((s) => s.keyframes);
  const selectedKeyframeId = useRaceTrackStore((s) => s.selectedKeyframeId);
  const isPlaying = useRaceTrackStore((s) => s.isPlaying);
  const isPreviewOpen = useRaceTrackStore((s) => s.isPreviewOpen);
  const backupKeyframes = useRaceTrackStore((s) => s.backupKeyframes);

  const hasKeyframes = keyframes.length > 0;
  const canPlay = keyframes.length >= 2;
  const playTitle = canPlay
    ? isPlaying
      ? t('raceTrack.pause')
      : t('raceTrack.play')
    : t('raceTrack.needKeyframes', { count: keyframes.length });
  const hasSelected = selectedKeyframeId !== null;

  return (
    <div className="mb-2 flex items-center justify-between gap-2 px-4 lg:px-10">
      {/* 左侧：预览开关 + 运镜参数 */}
      <div className="flex items-center gap-2">
        <ToolButton
          title={isPreviewOpen ? t('raceTrack.hidePreview') : t('raceTrack.showPreview')}
          onPress={onTogglePreview}
          className={isPreviewOpen ? 'text-white' : ''}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </ToolButton>
      </div>

      {/* 中间：关键帧 + 播放控制 */}
      <div className="flex items-center justify-center gap-2">
        <div className="flex items-center gap-0.5">
          <ToolButton title={t('raceTrack.addKeyframe')} onPress={onCapture}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 8v8" />
              <path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0z" />
              <path d="M8 12h8" />
            </svg>
          </ToolButton>
          <ToolButton
            title={t('raceTrack.updateKeyframe')}
            disabled={!hasSelected}
            onPress={onUpdateSelected}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
              <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
              <circle cx="12" cy="12" r="3" />
              <path d="m18 22-3-3 3-3" />
              <path d="m6 2 3 3-3 3" />
            </svg>
          </ToolButton>
          <ToolButton
            title={t('raceTrack.deleteSelected')}
            disabled={!hasSelected}
            onPress={onDeleteSelected}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0z" />
              <path d="M8 12h8" />
            </svg>
          </ToolButton>
          <ToolButton
            title={t('raceTrack.deleteAll')}
            disabled={!hasKeyframes}
            onPress={onDeleteAll}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          </ToolButton>
        </div>

        <div className="mx-1 h-6 w-px bg-white/20" />

        <div className="flex items-center gap-0.5">
          <ToolButton
            title={t('raceTrack.jumpToStart')}
            disabled={!hasKeyframes}
            onPress={onJumpToStart}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.971 4.285A2 2 0 0 1 21 6v12a2 2 0 0 1-3.029 1.715l-9.997-5.998a2 2 0 0 1-.003-3.432z" />
              <path d="M3 20V4" />
            </svg>
          </ToolButton>
          <ToolButton
            title={t('raceTrack.jumpToPrevious')}
            disabled={!hasKeyframes}
            onPress={onJumpToPrevious}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M13.971 4.285A2 2 0 0 1 17 6v12a2 2 0 0 1-3.029 1.715l-9.997-5.998a2 2 0 0 1-.003-3.432z" />
              <path d="M21 20V4" />
            </svg>
          </ToolButton>
          <ToolButton
            title={playTitle}
            disabled={!canPlay}
            onPress={onPlayToggle}
            className="text-white"
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
              </svg>
            )}
          </ToolButton>
          <ToolButton
            title={t('raceTrack.jumpToNext')}
            disabled={!hasKeyframes}
            onPress={onJumpToNext}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.029 4.285A2 2 0 0 0 7 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z" />
              <path d="M3 4v16" />
            </svg>
          </ToolButton>
          <ToolButton
            title={t('raceTrack.jumpToEnd')}
            disabled={!hasKeyframes}
            onPress={onJumpToEnd}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 4v16" />
              <path d="M6.029 4.285A2 2 0 0 0 3 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z" />
            </svg>
          </ToolButton>
        </div>
      </div>

      {/* 右侧：优化 + 导出 + 关闭编辑 */}
      <div className="flex items-center gap-2">
        <ToolButton
          title={backupKeyframes ? t('raceTrack.undoOptimize') : t('raceTrack.optimizeTrack')}
          disabled={!canPlay}
          onPress={onOptimize}
          className={backupKeyframes ? 'text-blue-400' : 'text-white'}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {backupKeyframes ? (
              <>
                <path d="M3 7v6h6" />
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
              </>
            ) : (
              <>
                <path d="M12 2v4" />
                <path d="m16.24 7.76 2.83-2.83" />
                <path d="M18 12h4" />
                <path d="m16.24 16.24 2.83 2.83" />
                <path d="M12 18v4" />
                <path d="m4.93 19.07 2.83-2.83" />
                <path d="M2 12h4" />
                <path d="m4.93 4.93 2.83 2.83" />
              </>
            )}
          </svg>
        </ToolButton>

        <ToolButton
          title={t('raceTrack.exportVideo')}
          disabled={!canPlay}
          onPress={onExport}
          className="text-white"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 15V3" />
            <path d="M7 10l5 5 5-5" />
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          </svg>
        </ToolButton>

        <div className="mx-1 h-6 w-px bg-white/20" />

        <ToolButton title={t('raceTrack.closeEditor')} onPress={onClose} className="text-white">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </ToolButton>
      </div>
    </div>
  );
}
